export const state = () => ({
	user: null,
	multiFactorAuth: false,
	totpError: null
})
const initialState = Object.assign({}, state())

export const getters = {
	isAuthenticated ({ user }) {
		return Boolean(user)
	},
	isEmailVerified ({ user }, getters) {
		return getters.isAuthenticated && user.emailVerified
	},
	hasUserEmail ({ user }, getters) {
		return getters.isAuthenticated && user.email && user.emailVerified
	},
	multiFactorAuth ({ multiFactorAuth }) {
		return multiFactorAuth
	},
	totpError ({ totpError }) {
		return totpError
	}
}

export const actions = {
	async createFirebaseUser ({ dispatch, commit }, userData) {
		try {
			const { email, password, name: displayName, association, skipVerificationEmail } = userData
			const currentUser = await this.$firebase.signup(email, password, 'session')

			if (displayName) {
				await dispatch('updateFirebaseUserProfile', { displayName })
			}

			const user = this.$firebase.getUserFormatted(currentUser)

			// await currentUser.updateProfile({ displayName })

			this.$log.info('[AuthStore] Created `firebase user`', user)

			commit('setUser', user)

			if (!skipVerificationEmail) {
				dispatch('sendVerificationEmail')
			}

			if (association) {
				await this.$axios.post('/v1/auth/user', { association })
			}
		} catch (error) {
			if (error.code) {
				throw new Error(this.$i18n.tc(`authorization.${error.code}`), { cause: error.code })
			} else {
				throw new Error(error.message)
			}
		}
	},

	async createAnonymousFirebaseUser ({ commit, dispatch }, userObj) {
		try {
			const { email, firstName, lastName } = userObj

			if (email) {
				await dispatch('checkUserSignInMethods', email)
			}

			const displayName = firstName && lastName ? `${firstName} ${lastName}` : ''
			const currentUser = await this.$firebase.signUpAnonymously()
			if (displayName) {
				await dispatch('updateFirebaseUserProfile', { displayName })
			}
			const user = this.$firebase.getUserFormatted(currentUser)

			this.$log.info('[AuthStore] Created anonymous `firebase user`', user)

			commit('setUser', user)
		} catch (error) {
			const emailAlreadyExistsCode = 'auth/email-already-in-use'

			if (error.code || error.cause === emailAlreadyExistsCode) {
				const message = error.code ? this.$i18n.tc(`authorization.${error.code}`) : error.message

				throw new Error(message, { cause: emailAlreadyExistsCode ? error.cause : error.code })
			} else {
				throw new Error(error.message)
			}
		}
	},

	async checkUserSignInMethods ({ commit }, email) {
		// this check should be moved to BE
		const signInMethods = await this.$firebase.getUserSignInMethods(email)

		if (signInMethods.length) {
			// this is only 1 method to check if user exists without calling sign in-up actions
			throw new Error(this.$i18n.tc('authorization.auth/email-already-exists'), { cause: 'auth/email-already-in-use' })
		}
	},

	async signUp ({ rootState, dispatch }, { userData, teamData, selection, accountType, isInvitedMember, acceptedPolicies }) {
		try {
			const paymentUser = {
				clubName: userData.clubName,
				phone: userData.phone,
				address: userData.address,
				selection,
				meta: {
					accountType,
					campaign: rootState.user.utm,
					role: 'admin',
					teamRole: teamData.role,
					userSource: teamData.hearFrom
				}
			}

			const orgData = {
				name: `${userData.email?.split('@')?.[0] || 'N/A'} organization`,
				type: 'other',
				location: {
					country: userData.address?.country_code || 'XX',
					state: userData.address?.state || undefined,
					city: userData.address?.city || 'N/A'
				}
			}

			// Remove country_code needed for org creation due to account creation issue
			if (userData?.address?.country_code) {
				delete userData.address.country_code
			}

			if (!isInvitedMember) {
				await dispatch('createFirebaseUser', userData)
			}
			await dispatch('user/createPaymentUser', paymentUser, { root: true })
			const identifyData = { accountType, last_sign_method: 'credentials' }
			if (acceptedPolicies) {
				identifyData.email_subscribe = 'opted_in'
			}
			await dispatch('analytics/identify', identifyData, { root: true })
			// analytics goes first before team creation as it sends segment identify with userId
			await dispatch('analytics/signUp', { userData, teamData, accountType }, { root: true })
			if (process.env.features.enable_org_creation) {
				await dispatch('organizations/createOrganization', orgData, { root: true })
			}
			await dispatch('teams/createTeam', teamData, { root: true })
		} catch (error) {
			throw new Error(error.message, { cause: error.cause })
		}
	},

	async login ({ dispatch, commit }, payload) {
		if (getters.isAuthenticated) {
			dispatch('logout')
		}

		const { credentials: { email, password }, rememberMe, allowUnverified } = payload
		const persistance = rememberMe ? 'local' : 'session'

		try {
			const currentUser = await this.$firebase.login(email, password, persistance)
			const user = this.$firebase.getUserFormatted(currentUser)

			commit('setUser', user)

			if (!allowUnverified && !user.emailVerified) {
				throw new Error(this.$i18n.tc('errors.failedLoginError'))
			}
			dispatch('analytics/identify', { last_sign_method: 'credentials' }, { root: true })
		} catch (error) {
			if (error.code) {
				if (error.code === 'auth/multi-factor-auth-required') {
					throw error
				}

				throw new Error(this.$i18n.tc(`authorization.${error.code}`))
			} else {
				throw new Error(error.message)
			}
		}
	},

	async logInWithTotp ({ commit }, payload) {
		const { code, error } = payload
		try {
			const mfaResolver = this.$firebase.getMultiFactorResolver(
				this.$firebase.getAuth(),
				error
			)

			const enrolledFactors = mfaResolver.hints

			const totpFactor = enrolledFactors.find(
				factor => factor.factorId === this.$firebase.TotpMultiFactorGenerator.FACTOR_ID
			)

			if (totpFactor) {
				const multiFactorAssertion =
                    this.$firebase.TotpMultiFactorGenerator.assertionForSignIn(
                    	totpFactor.uid,
                    	code
                    )

				const data = await mfaResolver.resolveSignIn(multiFactorAssertion)
				const currentUser = data.user

				const user = this.$firebase.getUserFormatted(currentUser)

				commit('setUser', user)
			}
		} catch (error) {
			if (error.code) {
				throw new Error(this.$i18n.tc(`authorization.${error.code}`))
			} else {
				throw new Error(error.message)
			}
		}
	},

	signInWithSocialProvider ({ commit, dispatch }, provider) {
		const loginUser = (currentUser) => {
			commit('setUser', this.$firebase.getUserFormatted(currentUser))
			dispatch('analytics/identify', { linkedProvider: provider, last_sign_method: provider }, { root: true })
		}

		return this.$firebase.loginWithProvider(provider, loginUser)
	},

	async logout ({ state, commit, dispatch }) {
		try {
			commit('RESET_STORE')
			commit('user/RESET_STORE', null, { root: true })
			commit('teams/RESET_STORE', null, { root: true })
			commit('events/RESET_STORE', null, { root: true })
			commit('products/setWizardSelection', null, { root: true })

			await this.$firebase.logout()
			this.$axios.setToken(false)
			dispatch('analytics/reset', null, { root: true })
			this.$log.debug('[AuthStore] Logout action dispatched', state.user)
		} catch (error) {
			// throw new Error(this.$i18n.tc(`authorization.${error.code}`))
			throw new Error('An error occurred while trying to log out')
		}
	},

	async reauthenticateUser (store, password) {
		try {
			await this.$firebase.reauthenticate(password)
		} catch (error) {
			if (error.code) {
				throw new Error(this.$i18n.tc(`authorization.${error.code}`))
			} else {
				throw new Error(error.message)
			}
		}
	},

	resetPassword (store, email) {
		return this.$firebase.resetPassword(email)
	},

	verifyEmailRecoverActionCode (store, actionCode) {
		return this.$firebase.applyEmailRecover(actionCode)
	},

	verifyEmailActionCode ({ commit }, actionCode) {
		return this.$firebase.applyEmailVerify(actionCode).then(() => {
			commit('setEmailVerified', true)
		})
	},

	revertSecondFactorAddition (store, actionCode) {
		return this.$firebase.revertSecondFactorAddition(actionCode)
	},

	sendVerificationEmail () {
		return this.$firebase.sendEmailVerification()
	},

	updateFirebaseUserEmail ({ commit }, email) {
		return this.$firebase.updateUserEmail(email).then(async () => {
			const user = await this.$firebase.getUser(true)
			commit('setUser', user)
		})
	},

	updateFirebaseUserProfile ({ commit }, profile) {
		return this.$firebase.updateUserProfile(profile).then(async () => {
			const user = await this.$firebase.getUser(true)
			commit('setUser', user)
		})
	}
}

export const mutations = {
	RESET_STORE: (state) => {
		Object.assign(state, initialState)
	},

	setUser (state, user) {
		state.user = user || null
	},

	setEmailVerified (state, value = false) {
		if (state.user) {
			state.user.emailVerified = value
		}
	},

	setMultiFactorAuth (state, value = false) {
		state.multiFactorAuth = value
	},

	setTotpError (state, error) {
		state.totpError = error
	}
}
